import { wxShares, wxOpenId } from '@/api/index.js';
import { trackEvent } from '@/common/analytics.js';
import { getStorageSync, setStorageSync } from '@/utils/storage.ts';

// 访问事件追踪 - 最后访问时间 key
const VISIT_EVENT_KEY = 'last_visit_event_time';

/**
 * 是否在微信内置浏览器
 */
export function isWechatEnv() {
	if (typeof navigator === 'undefined') return false;
	return /micromessenger/i.test(navigator.userAgent);
}

/**
 *  判断微信/企业微信环境的方法
 */
export const getEnvironmentType = () => {
	if (typeof navigator === 'undefined') return 0;
	const ua = navigator.userAgent.toLowerCase();
	if (ua.includes('wxwork')) {
		return 2; // 企业微信
	} else if (ua.includes('micromessenger')) {
		return 1; // 微信
	}
};

/**
 * 获取微信基本信息（调用 /cms/public/share）  分享
 * @returns {Promise<{ appId, timestamp, nonceStr, signature } | null>}
 */
export async function getWechatConfig() {
	// 不缓存！跨页面路由时 URL 变化，缓存的签名会不匹配导致 wx.config 失败
	try {
		if (typeof window === 'undefined') return null;
		const url = new URL(window.location.href);
		url.hash = '';
		const res = await wxShares({ url: url.href });
		if (res.code == 200) return res.data;
		return null;
	} catch (error) {
		console.error('获取微信分享配置失败:', error);
		return null;
	}
}

/**
 * 初始化微信 JSSDK（调用 getWechatConfig + wx.config）
 * 由 composables/useWeixin 在客户端首次使用时调用
 * @param {Object} wx - jweixin 实例
 */
export async function initConfig(wx) {
	if (!wx?.config || !isWechatEnv()) return;

	if (getEnvironmentType() == 2) return;

	try {
		const config = await getWechatConfig();
		if (config) {
			wx.config({
				debug: false,
				appId: config.appId,
				timestamp: config.timestamp,
				nonceStr: config.nonceStr,
				signature: config.signature,
				jsApiList: [
					'scanQRCode',
					'scanCode',
					'chooseImage',
					'uploadImage',
					'updateAppMessageShareData',
					'updateTimelineShareData',
					'onMenuShareTimeline', //（ 即将废弃）
					'onMenuShareAppMessage', //（ 即将废弃）
					'onMenuShareQQ', //（ 即将废弃）
					'onMenuShareWeibo',
					'onMenuShareQZone',
					'getLocation',
					'closeWindow',
				],
			});
		}
	} catch (e) {
		console.error('微信 SDK 初始化失败:', e);
	}
}

/**
 * 获取微信 code 并完成授权（无 code 时跳转授权页）
 * @returns {Promise<string>} 已有 code 时返回 code，否则跳转后返回 ''
 */
export async function getWechatCode() {
	if (typeof window === 'undefined') return '';
	if (!isWechatEnv()) return '';

	const envType = getEnvironmentType();

	// 只在微信环境（非企业微信）下处理授权
	if (envType !== 1) return '';

	// 如果已有 openId，直接追踪访问并返回
	if (getStorageSync('user_openCode')) {
		const openId = getStorageSync('user_openCode');
		trackWechatVisit(envType, openId);
		return '';
	}

	// 获取 URL 中的 code
	const url = new URL(window.location.href);
	const code = url.searchParams.get('code');
	if (code) {
		// 有 code，调用 wxOpenId 交换获取 openId
		setStorageSync('wxCode', code);
		try {
			const res = await wxOpenId({ code });
			if (res?.data) {
				setStorageSync('user_openCode', res.data);
				console.log('%c [ openId 相关 ]', 'font-size:13px; background:#5d5661; color:#a19aa5;', res.data);
				trackWechatVisit(getEnvironmentType(), res.data);
			}
		} catch (e) {
			console.error('获取 openId 失败:', e);
		}

		// 清理 URL 中的 code/state，避免 SSR 解析报错
		url.searchParams.delete('code');
		url.searchParams.delete('state');
		window.history.replaceState({}, '', url.toString());

		return code;
	}

	// 无 code，跳转微信授权页
	const config = await getWechatConfig();
	if (!config) {
		console.error('无法获取微信配置，无法授权');
		return '';
	}

	// 对标旧项目：编码完整当前 URL 作为回调地址
	const redirectUri = encodeURIComponent(window.location.href);
	const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${config.appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`;
	window.location.href = authUrl;
	return '';
}

// ==================== 访问追踪相关 ====================

/**
 * 检查两个日期是否是同一天（00:00 - 23:59）
 */
const isSameDay = (date1, date2) => {
	return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

/**
 * 检查今天是否已经发送过访问事件
 */
const hasSentToday = () => {
	const lastEventTime = getStorageSync(VISIT_EVENT_KEY);
	if (!lastEventTime) return false;
	const lastDate = new Date(lastEventTime);
	const now = new Date();
	return isSameDay(now, lastDate);
};

/**
 * 更新最后发送事件时间
 */
const updateLastVisitEventTime = () => {
	setStorageSync(VISIT_EVENT_KEY, Date.now());
};

/**
 * 发送微信/企业微信访问追踪事件
 * 每天只发送一次（由 hasSentToday 控制）
 * @param {number} envType - 1: 微信, 2: 企业微信
 * @param {string} openId - 用户 openId
 */
function trackWechatVisit(envType, openId) {
	// 检查今天是否已经发送过事件
	if (hasSentToday()) {
		console.log('今天已发送过访问事件，跳过发送');
		return;
	}

	const envTypeStr = envType.toString();
	const visitData = {
		module: envTypeStr == 1 ? '微信用户' : '企业微信用户',
		eventCode: 'm-wechat-enter',
		eventName: envTypeStr == 1 ? '微信用户访问' : '企业微信用户访问',
		ext1: envTypeStr, // 1: 微信, 2: 企业微信
	};

	if (envTypeStr == 1) {
		visitData.ext2 = openId; // 微信：openId 放在 ext2
	} else {
		visitData.ext3 = openId; // 企业微信：openId 放在 ext3
	}

	trackEvent(visitData);
	console.log('发送访问事件:', visitData);

	// 更新最后发送事件时间
	updateLastVisitEventTime();
}

/**
 * 获取 openId 并发送访问事件
 * 用于需要主动获取 openId 并追踪访问的场景
 * @returns {Promise<{ envType: number, openId: string | null }>}
 */
export async function getOpenIdAndTrackEvent() {
	if (typeof window === 'undefined') return { envType: 0, openId: null };

	try {
		const envType = getEnvironmentType();

		if (envType === 1 || envType === 2) {
			// 先获取 code / openId（如果还没有的话）
			await getWechatCode();

			// 获取存储的 openId
			const openId = getStorageSync('user_openCode');
			if (openId) {
				return { envType, openId };
			} else {
				console.log('未获取到openId');
				return { envType, openId: null };
			}
		} else {
			console.log('非微信环境，不进行访问追踪');
			return { envType: 0, openId: null };
		}
	} catch (error) {
		console.error('获取openId并追踪事件失败:', error);
		return { envType: 0, openId: null };
	}
}
